import type { EffectivePermissions, SidebarNavConfig } from '@/lib/permissionTypes';

const KEY = 'bb-staff-shell';
const VERSION = 1;
/** Restored browser sessions can outlive a workday. Revalidate still runs immediately. */
const TTL_MS = 12 * 60 * 60 * 1000;

export interface CachedStaffShell {
  userId: string;
  permissions: EffectivePermissions;
  nav: SidebarNavConfig;
  savedAt: number;
}

interface StoredStaffShell extends CachedStaffShell {
  v: number;
}

function storage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function parseStored(raw: string | null, now: number): CachedStaffShell | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredStaffShell;
    if (parsed?.v !== VERSION) return null;
    if (!parsed.userId || typeof parsed.savedAt !== 'number') return null;
    if (now - parsed.savedAt > TTL_MS) return null;
    if (!parsed.permissions || !parsed.nav) return null;
    return {
      userId: parsed.userId,
      permissions: parsed.permissions,
      nav: parsed.nav,
      savedAt: parsed.savedAt,
    };
  } catch {
    return null;
  }
}

/** Any fresh shell in this tab, before the auth profile has a user id. */
export function peekStaffShellCache(now = Date.now()): CachedStaffShell | null {
  const store = storage();
  if (!store) return null;
  try {
    return parseStored(store.getItem(KEY), now);
  } catch {
    return null;
  }
}

export function readStaffShellCache(userId: string, now = Date.now()): CachedStaffShell | null {
  if (!userId) return null;
  const cached = peekStaffShellCache(now);
  if (!cached || cached.userId !== userId) return null;
  return cached;
}

export function writeStaffShellCache(shell: CachedStaffShell) {
  const store = storage();
  if (!store || !shell.userId) return;
  const stored: StoredStaffShell = { v: VERSION, ...shell };
  try {
    store.setItem(KEY, JSON.stringify(stored));
  } catch {
    /* quota / private mode */
  }
}

export function clearStaffShellCache() {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
